/**
 * DealScorePage — /v3/intel/deal-score
 *
 * V3-native surface for BP-005 Property Deal Score. Presents the
 * capped 0-100 score with band + label, the 5 weighted category
 * subscores + their factor breakdowns, data-gap warnings, caps
 * applied, AI review, and version history.
 *
 * Uses the existing engine endpoints. No new business logic.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUpRight, ChevronDown, ClipboardCheck, FileDown, Loader2,
  RefreshCw, Sparkles, TrendingUp, TrendingDown, Minus,
  AlertTriangle, ShieldAlert, Info, History,
} from "lucide-react";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import useSelectedProperty from "@/hooks/useSelectedProperty";

// ─────────────────────────────────────────── helpers ────────────────────

const BAND_META = {
  strong:   { label: "STRONG",     ringColor: "#10B981" },
  good:     { label: "GOOD",       ringColor: "#059669" },
  review:   { label: "REVIEW",     ringColor: "#F59E0B" },
  weak:     { label: "WEAK",       ringColor: "#EA580C" },
  pass:     { label: "PASS",       ringColor: "#DC2626" },
  excellent:{ label: "EXCELLENT",  ringColor: "#059669" },
};

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

function ImpactIcon({ impact }) {
  if (impact === "positive") return <TrendingUp className="w-3 h-3 text-emerald-600" />;
  if (impact === "negative") return <TrendingDown className="w-3 h-3 text-rose-600" />;
  if (impact === "unknown")  return <Info className="w-3 h-3 text-slate-400" />;
  return <Minus className="w-3 h-3 text-slate-400" />;
}

function subscoreColor(sub) {
  const s = Number(sub) || 0;
  if (s >= 80) return "#10B981";
  if (s >= 60) return "#F59E0B";
  if (s >= 40) return "#EA580C";
  return "#DC2626";
}

// ─────────────────────────────────────────── page ──────────────────────

export default function DealScorePage() {
  const [properties, setProperties] = useState([]);
  const { propertyId, setPropertyId } = useSelectedProperty(properties);
  const [loadingProps, setLoadingProps] = useState(true);

  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [notes, setNotes] = useState("");

  // Bootstrap workspace properties.
  useEffect(() => {
    let alive = true;
    api.get("/v3/properties?limit=200").then(({ data }) => {
      if (!alive) return;
      const list = data?.items || [];
      setProperties(list);
    }).catch(() => toast.error("Couldn't load properties"))
      .finally(() => { if (alive) setLoadingProps(false); });
    return () => { alive = false; };
  }, []);

  // Load current + history for the selected property.
  const load = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const [c, h] = await Promise.all([
        api.get(`/v3/properties/${pid}/deal-score`).then((r) => r.data).catch(() => null),
        api.get(`/v3/properties/${pid}/deal-score/history`).then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      // Handle empty {} response from get_current when no score exists.
      setCurrent(c && c.score != null ? c : null);
      setHistory(h?.items || []);
    } catch {
      setCurrent(null); setHistory([]);
    }
  }, []);
  useEffect(() => { load(propertyId); }, [propertyId, load]);

  const recalc = useCallback(async () => {
    if (!propertyId) return;
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${propertyId}/deal-score/calculate`,
        { notes: notes.trim() || null },
      );
      setCurrent(data);
      setNotes("");
      // Refresh history.
      const h = await api.get(`/v3/properties/${propertyId}/deal-score/history`)
        .then((r) => r.data).catch(() => ({ items: [] }));
      setHistory(h?.items || []);
      toast.success(`Score recalculated — v${data.version}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Recalculation failed");
    } finally {
      setCalculating(false);
    }
  }, [propertyId, notes]);

  const runAi = useCallback(async () => {
    if (aiLoading) return;
    if (!current?.id) { toast.error("Calculate a score first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/deal-score/${current.id}/ai-review`);
      setCurrent(data);
      toast.success("AI review complete");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI review failed");
    } finally {
      setAiLoading(false);
    }
  }, [current, aiLoading]);

  const exportPdf = useCallback(() => {
    if (!current?.id) { toast.error("Calculate a score first."); return; }
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/deal-score/${current.id}/export/pdf`,
      "_blank",
    );
  }, [current]);

  const band = current?.band;
  const bandMeta = band ? BAND_META[band] : null;
  const categories = current?.categories || [];
  const capsApplied = current?.caps_applied || [];
  const dataGaps = current?.data_gap_categories || [];
  const warnings = current?.warnings || [];
  const ai = current?.ai_review || null;

  return (
    <div className="space-y-6" data-testid="deal-score-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
            Intelligence Engine
          </div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            Deal Score
          </h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
            The single number that answers <b>“Should I chase this deal?”</b> — weighted
            profitability, condition, market, seller motivation, and risk signals.
          </p>
        </div>
        <PropertyPicker
          properties={properties}
          value={propertyId}
          onChange={setPropertyId}
          loading={loadingProps}
        />
      </div>

      {/* Empty state — no properties */}
      {!loadingProps && properties.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
             data-testid="deal-score-empty-no-props">
          <ClipboardCheck className="w-8 h-8 mx-auto mb-3 text-amber-500" />
          <div className="text-slate-900 font-bold text-lg">No properties yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-5">
            Deal Score analyses individual properties — add one first.
          </div>
          <Link to="/v3/crm/pipeline"
                className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            Open Pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {propertyId && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(300px,1fr)] gap-4">
          {/* ─── MAIN SCORE + CATEGORIES ─── */}
          <section
            data-testid="deal-score-main-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="h-1 w-full"
                 style={{ backgroundColor: bandMeta?.ringColor || "#334155" }} />

            {/* Score hero */}
            <div className="p-5 flex items-center gap-5 border-b border-slate-100">
              <DailyBusinessScore
                score={current?.capped_score ?? current?.score ?? 0}
                size="lg"
                label="Deal score"
                testid="deal-score-ring"
              />
              <div className="min-w-0 flex-1">
                {current ? (
                  <>
                    {bandMeta && (
                      <span
                        data-testid="deal-score-band-chip"
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1"
                        style={{
                          backgroundColor: `${bandMeta.ringColor}15`,
                          color: bandMeta.ringColor,
                          borderColor: `${bandMeta.ringColor}40`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: bandMeta.ringColor }} />
                        {current.label || bandMeta.label}
                      </span>
                    )}
                    <div className="mt-1.5 text-[15px] font-bold text-slate-900" data-testid="deal-score-title">
                      v{current.version} · Scored {timeAgo(current.created_at)}
                    </div>
                    {current.notes && (
                      <div className="mt-1 text-[12px] text-slate-500 line-clamp-2">
                        “{current.notes}”
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-[15px] font-bold text-slate-900">
                      Not scored yet
                    </div>
                    <div className="mt-1 text-[13px] text-slate-500">
                      Hit <b>Score this deal</b> below to run the weighted classifier.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recalculate row */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  Notes (optional · for the audit trail)
                </div>
                <input
                  type="text" value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Post-walkthrough re-score"
                  data-testid="deal-score-notes-input"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
              </div>
              <button type="button"
                      onClick={recalc}
                      disabled={calculating}
                      data-testid="deal-score-recalc-btn"
                      className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md disabled:opacity-60"
                      style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {current ? "Re-score this deal" : "Score this deal"}
              </button>
            </div>

            {/* Category cards */}
            {categories.length > 0 && (
              <div className="p-5 space-y-3" data-testid="deal-score-categories">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Category breakdown
                </div>
                {categories.map((cat) => (
                  <CategoryCard key={cat.id} cat={cat} />
                ))}
              </div>
            )}
          </section>

          {/* ─── SIDE PANEL: caps, gaps, warnings, actions, history, AI ─── */}
          <div className="space-y-4 self-start lg:sticky lg:top-4">
            {/* Actions */}
            <section
              data-testid="deal-score-actions-panel"
              className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
                  Actions
                </div>
              </div>
              <div className="p-3 space-y-2">
                <button type="button"
                        onClick={runAi}
                        disabled={aiLoading || !current}
                        data-testid="deal-score-ai-btn"
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[12.5px] font-semibold px-3 py-2 disabled:opacity-60">
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Run AI Review
                </button>
                <button type="button"
                        onClick={exportPdf}
                        disabled={!current}
                        data-testid="deal-score-pdf-btn"
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-2 disabled:opacity-60">
                  <FileDown className="w-4 h-4" /> Export PDF
                </button>
              </div>
            </section>

            {/* Caps applied */}
            {capsApplied.length > 0 && (
              <section
                data-testid="deal-score-caps-panel"
                className="rounded-2xl border border-rose-200/60 bg-rose-50/40 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-rose-200/60 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-rose-600" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
                    Caps applied ({capsApplied.length})
                  </div>
                </div>
                <div className="p-3 space-y-1.5">
                  {capsApplied.map((c, i) => (
                    <div key={i} className="text-[11.5px] text-slate-700 leading-snug"
                         data-testid={`deal-score-cap-${c.rule || i}`}>
                      <b className="text-rose-700">{c.rule || "Cap"}</b>
                      {c.max_score != null && (
                        <span className="text-slate-500"> · capped at {c.max_score}</span>
                      )}
                      {c.reason && <div className="text-[11px] text-slate-500">{c.reason}</div>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Data gaps */}
            {dataGaps.length > 0 && (
              <section
                data-testid="deal-score-gaps-panel"
                className="rounded-2xl border border-amber-200/70 bg-amber-50/40 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-amber-200/70 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                    Data gaps ({dataGaps.length})
                  </div>
                </div>
                <div className="p-3 flex flex-wrap gap-1">
                  {dataGaps.map((g) => (
                    <span key={g}
                          data-testid={`deal-score-gap-${g}`}
                          className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      {g}
                    </span>
                  ))}
                </div>
                <div className="px-3 pb-3 text-[11px] text-slate-600 leading-relaxed">
                  These categories are running on defaults. Fill in the underlying data
                  (repairs, comps, seller intake) for a sharper score.
                </div>
              </section>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <section
                data-testid="deal-score-warnings-panel"
                className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Info className="w-4 h-4 text-slate-500" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Warnings ({warnings.length})
                  </div>
                </div>
                <div className="p-3 space-y-1.5">
                  {warnings.map((w, i) => (
                    <div key={i} className="text-[11.5px] text-slate-700 leading-snug flex items-start gap-1.5"
                         data-testid={`deal-score-warn-${w.code || i}`}>
                      <Info className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                      <span>{w.message || String(w)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* History */}
            {history.length > 0 && (
              <section
                data-testid="deal-score-history-panel"
                className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Score history ({history.length})
                  </div>
                </div>
                <div className="max-h-[240px] overflow-y-auto divide-y divide-slate-50">
                  {history.slice(0, 12).map((h) => {
                    const meta = BAND_META[h.band] || BAND_META.review;
                    return (
                      <div key={h.id}
                           data-testid={`deal-score-history-${h.id}`}
                           className="px-3 py-2 flex items-center gap-2 hover:bg-amber-50/40">
                        <span
                          className="text-[10px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-md"
                          style={{
                            backgroundColor: `${meta.ringColor}15`,
                            color: meta.ringColor,
                          }}>
                          {h.capped_score ?? h.score}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11.5px] font-semibold text-slate-800 truncate">
                            v{h.version} · {h.label}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {timeAgo(h.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {/* AI review (full width) */}
      {ai && (
        <section
          data-testid="deal-score-ai-panel"
          className="rounded-2xl border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden"
        >
          <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
          <div className="px-5 py-4 border-b border-amber-200/70 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                AI Review · Claude Sonnet 4.6
              </div>
              <div className="text-[14px] font-bold text-slate-900 leading-tight">
                Reviewer notes on this deal
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3 text-[13px] text-slate-800 leading-relaxed">
            {ai.summary && (
              <p data-testid="deal-score-ai-summary" className="whitespace-pre-line">
                {ai.summary}
              </p>
            )}
            {Array.isArray(ai.strengths) && ai.strengths.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">Strengths</div>
                <ul className="space-y-1">
                  {ai.strengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(ai.risks) && ai.risks.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 mb-1">Risks</div>
                <ul className="space-y-1">
                  {ai.risks.map((s, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(ai.next_actions) && ai.next_actions.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">Next actions</div>
                <ul className="space-y-1">
                  {ai.next_actions.map((s, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <ArrowUpRight className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── CategoryCard ──────────────

function CategoryCard({ cat }) {
  const [open, setOpen] = useState(false);
  const color = subscoreColor(cat.subscore);
  return (
    <div
      data-testid={`deal-score-category-${cat.id}`}
      className={[
        "rounded-xl border border-slate-200/70 bg-white overflow-hidden transition-shadow",
        cat.data_gap ? "ring-1 ring-amber-200" : "",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`deal-score-category-${cat.id}-toggle`}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-amber-50/30 transition-colors"
      >
        {/* Subscore chip */}
        <div className="shrink-0 h-10 w-10 rounded-lg grid place-items-center font-black text-white tabular-nums text-[15px]"
             style={{ backgroundColor: color }}
             data-testid={`deal-score-category-${cat.id}-subscore`}>
          {cat.subscore}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[13.5px] font-bold text-slate-900 flex items-center gap-2">
            {cat.label}
            {cat.data_gap && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                Data gap
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500">
            Weight {Math.round((cat.weight || 0) * 100)}% · {cat.factors?.length || 0} factors
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && cat.factors?.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/30">
          <div className="divide-y divide-slate-100">
            {cat.factors.map((f, i) => (
              <div key={i} className="px-4 py-2 flex items-center gap-3"
                   data-testid={`deal-score-factor-${cat.id}-${i}`}>
                <ImpactIcon impact={f.impact} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-slate-800 truncate">{f.label}</div>
                </div>
                <div className="text-[12px] font-bold text-slate-900 tabular-nums">
                  {f.value ?? "—"}
                </div>
                <div className="text-[10px] text-slate-400 w-10 text-right tabular-nums">
                  ×{Math.round((f.weight || 0) * 100)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── property picker ──────────

function PropertyPicker({ properties, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const selected = properties.find((p) => p.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return properties;
    const t = q.trim().toLowerCase();
    return properties.filter((p) => `${p.address?.street || ""} ${p.address?.city || ""}`.toLowerCase().includes(t));
  }, [properties, q]);
  return (
    <div ref={boxRef} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
              disabled={loading || properties.length === 0}
              data-testid="deal-score-property-picker"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:border-amber-400 px-3 py-2 text-[13px] font-semibold text-slate-800 shadow-sm min-w-[240px] justify-between disabled:opacity-60">
        <span className="truncate text-left">
          {loading ? "Loading…" : selected ? `${selected.address?.street || "Property"} · ${selected.address?.state || ""}` : "Select property"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div data-testid="deal-score-property-picker-menu"
             className="absolute right-0 top-full mt-1 w-[320px] max-h-[360px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl z-30 flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search address, city…" autoFocus
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-slate-500 text-center">No matches.</div>
            ) : filtered.map((p) => (
              <button key={p.id} type="button"
                      data-testid={`deal-score-property-picker-opt-${p.id}`}
                      onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-amber-50 border-b border-slate-50 ${p.id === value ? "bg-amber-50/60" : ""}`}>
                <div className="font-semibold text-slate-900 truncate">{p.address?.street || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
